<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Business Monitoring</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>Search Filters</v-card-title>
      <v-card-text>
        <v-form v-model="valid">
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model="startDate" type="datetime-local" label="Start Date (UTC)" density="comfortable" />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="endDate" type="datetime-local" label="End Date (UTC)" density="comfortable" />
            </v-col>
            <v-col cols="12" md="3"><v-text-field v-model="traceId" label="Trace Id:" density="comfortable" /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="vin" :rules="vinRules" label="VIN:" @paste="pasteVinFilter" density="comfortable" /></v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3"><v-text-field v-model="urlKeyword" label="Url Keyword:" density="comfortable" /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="payloadKeyword" label="Payload Keyword:" density="comfortable" /></v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col cols="12" class="d-flex align-center flex-wrap gap-3">
              <v-btn size="small" color="primary" :disabled="!valid" :loading="searchLoading" v-preventReClick @click="searchData">Search</v-btn>
              <v-btn size="small" variant="tonal" color="primary" :disabled="!valid" :loading="reloadLoading" v-preventReClick @click="reload">Reload</v-btn>
              <v-btn size="small" variant="outlined" color="primary" :disabled="!valid" :loading="downloadLoading" @click="exportCSV">Download</v-btn>
              <v-btn size="small" variant="text" color="secondary" @click="clear">Clear</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <template v-if="visible">
      <v-card class="section-card">
        <v-card-title class="info-title">CFES Business Monitoring Information ( If the "Start Date" and "End Date" are not set, the default response is the result of the last 8 hours, the max result size is 10000{{ searchResultCountText }})</v-card-title>
        <v-data-table
          dense
          show-expand
          v-model:expanded="logItemExpanded"
          :headers="logInformationHeaders"
          :items="logInformationSet"
          :items-per-page="-1"
          item-key="id"
          class="elevation-1 monitoring-table"
          @update:expanded="onExpandedChange"
          hide-default-footer
        >
          <template #expanded-row="{ item, columns }">
            <tr>
              <td :colspan="columns.length" class="pa-4">
                <v-card class="pa-2">
                  <v-tabs v-model="expandTabs[item.id]" bg-color="transparent">
                    <v-tab :value="0">Request Body</v-tab>
                    <v-tab :value="1">Response Body</v-tab>
                    <v-tab :value="2">Error Message</v-tab>
                    <v-tab v-if="item.requestUri && item.requestUri.startsWith('/cfes/fodhandler/licence/v1/assignments/vehicles/')" :value="3" @click="decodeProofOfPurchase(expandedData[item.id]?.requestBody, item)">ProofOfPurchase</v-tab>
                    <v-tab v-if="item.requestUri && item.requestUri.startsWith('/cfes/fodhandler/licence/v1/revocations/vehicles/')" :value="4" @click="decodeProofOfRevocation(expandedData[item.id]?.requestBody, item)">ProofOfRevocation</v-tab>
                    <v-tab v-if="item.requestUri && item.requestUri.startsWith('/cfes/fodhandler/licence/v1/terminations/vehicles/')" :value="7" @click="decodeTermination(expandedData[item.id]?.requestBody, item)">Termination Proofs</v-tab>
                    <v-tab v-if="expandedData[item.id]?.isStateReport" :value="5">STATE REPORT</v-tab>
                    <v-tab v-if="expandedData[item.id]?.isPreferredUsernameValue" :value="6">Preferred Username Value</v-tab>
                    <v-tab :value="7">Metadata</v-tab>
                  </v-tabs>
                  <v-window v-model="expandTabs[item.id]">
                    <v-window-item :value="0">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(expandedData[item.id]?.isRequestBodyJson ? JSON.stringify(expandedData[item.id]?.requestBody, null, 2) : expandedData[item.id]?.requestBody)">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.requestBody" />
                      </v-card>
                    </v-window-item>
                    <v-window-item :value="1">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(expandedData[item.id]?.isResponseBodyJson ? JSON.stringify(expandedData[item.id]?.responseBody, null, 2) : expandedData[item.id]?.responseBody)">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.responseBody" />
                      </v-card>
                    </v-window-item>
                    <v-window-item :value="2">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(expandedData[item.id]?.errorMessage)">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.errorMessage" />
                      </v-card>
                    </v-window-item>
                    <v-window-item v-if="item.requestUri && item.requestUri.includes('/cfes/fodhandler/licence/v1/assignments/vehicles/')" :value="3">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(JSON.stringify(item.decodedStr, null, 2))">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="item.decodedStr" />
                      </v-card>
                    </v-window-item>
                    <v-window-item v-if="item.requestUri && item.requestUri.includes('/cfes/fodhandler/licence/v1/revocations/vehicles/')" :value="4">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(JSON.stringify(item.decodedStr, null, 2))">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="item.decodedStr" />
                      </v-card>
                    </v-window-item>
                    <v-window-item v-if="item.requestUri && item.requestUri.includes('/cfes/fodhandler/licence/v1/terminations/vehicles/')" :value="7">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(JSON.stringify(item.decodedStr, null, 2))">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="item.decodedStr" />
                      </v-card>
                    </v-window-item>
                    <v-window-item v-if="expandedData[item.id]?.isStateReport" :value="5">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(JSON.stringify(expandedData[item.id]?.decodeStateReport, null, 2))">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.decodeStateReport" />
                      </v-card>
                    </v-window-item>
                    <v-window-item v-if="expandedData[item.id]?.isPreferredUsernameValue" :value="6">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(expandedData[item.id]?.preferredUsernameValue)">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.preferredUsernameValue" />
                      </v-card>
                    </v-window-item>
                    <v-window-item :value="7">
                      <v-card flat class="pa-2">
                        <div class="d-flex justify-end mb-2">
                          <v-btn size="small" variant="text" @click="copyToClipboard(expandedData[item.id]?.isMetadataJson ? JSON.stringify(expandedData[item.id]?.metadata, null, 2) : expandedData[item.id]?.metadata)">
                            <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
                          </v-btn>
                        </div>
                        <JsonViewer :value="expandedData[item.id]?.metadata" />
                      </v-card>
                    </v-window-item>
                  </v-window>
                </v-card>
              </td>
            </tr>
          </template>

          <template v-slot:[`item.createdTime`]="{ item }">
            <p class="table-createdTime">{{ formatDate(item.createdTime) }}</p>
          </template>
          <template v-slot:[`item.vin`]="{ item }">
            <p class="table-vin">{{ item.vin }}</p>
          </template>
          <template v-slot:[`item.traceId`]="{ item }">
            <p class="table-traceId">{{ item.traceId }}</p>
          </template>
          <template v-slot:[`item.requestUri`]="{ item }">
            <p class="uri-cell">{{ item.requestUri }}</p>
          </template>
          <template v-slot:[`item.responseTime`]="{ item }">
            <p class="table-responseTime">{{ item.responseTime }}</p>
          </template>
          <template v-slot:[`item.responseCode`]="{ item }">
            <p class="table-responseCode d-flex align-center justify-center">{{ item.responseCode }}</p>
          </template>
          <template v-slot:[`item.direction`]="{ item }">
            <p class="d-flex align-center justify-center">{{ item.direction }}</p>
          </template>
          <template v-slot:[`item.protocol`]="{ item }">
            <p class="d-flex align-center justify-center">{{ item.protocol }}</p>
          </template>

          <!-- Header filter dropdowns -->
          <template v-slot:[`header.responseCode`]="{ column }">
            <v-select
              v-model="responseCode"
              :items="response_code_list"
              item-title="name"
              item-value="value"
              :label="column.title"
              density="compact"
              hide-details
              variant="outlined"
              class="header-filter-select"
              @update:model-value="searchData"
            />
          </template>
          <template v-slot:[`header.direction`]="{ column }">
            <v-select
              v-model="direction"
              :items="direction_list"
              item-title="name"
              item-value="value"
              :label="column.title"
              density="compact"
              hide-details
              variant="outlined"
              class="header-filter-select"
              @update:model-value="searchData"
            />
          </template>
          <template v-slot:[`header.protocol`]="{ column }">
            <v-select
              v-model="protocol"
              :items="protocol_list"
              item-title="name"
              item-value="value"
              :label="column.title"
              density="compact"
              hide-details
              variant="outlined"
              class="header-filter-select"
              @update:model-value="searchData"
            />
          </template>
        </v-data-table>

        <v-row class="justify-center mt-4 align-center">
          <v-col cols="12" md="2" class="d-flex align-center">
            <span class="mr-2">Rows per page:</span>
            <v-select
              v-model="options.itemsPerPage"
              :items="itemsPerPageOptions"
              density="compact"
              hide-details
              style="max-width: 80px;"
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center justify-center">
            <v-btn size="small" variant="text" :disabled="options.page <= 1" @click="goFirstPage">
              <v-icon>mdi-page-first</v-icon>
            </v-btn>
            <v-btn size="small" variant="text" :disabled="options.page <= 1" @click="goPrevPage">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-4">Page {{ options.page }} of {{ pageCount }}</span>
            <v-btn size="small" variant="text" :disabled="options.page >= pageCount" @click="goNextPage">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn size="small" variant="text" :disabled="options.page >= pageCount" @click="goLastPage">
              <v-icon>mdi-page-last</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-text-field
              v-model="inputPageValue"
              label="Go to page"
              type="number"
              density="compact"
              hide-details
              style="max-width: 100px;"
              class="mr-2"
            />
            <v-btn size="small" @click="goToPage">Go</v-btn>
          </v-col>
        </v-row>
      </v-card>
    </template>

  </v-container>
</template>

<script>
import moment from 'moment-timezone'
import { toast } from '@/utils/toast'
import { copyToClipboard } from '@/utils/common'
import {
  businessMonitoringCleanUp,
  businessMonitoringDecodeStateReport,
  businessMonitoringExport,
  businessMonitoringGetCleanUp,
  businessMonitoringGetErrorPayload,
  businessMonitoringSearch,
  businessMonitoringCount,
} from '@/api/cfes/businessMonitoring'
import JsonViewer from '@/components/JsonViewer.vue'

export default {
  components: { JsonViewer },
  data: () => ({
    response_code_list: [
      { name: 'All', value: ' ' },
      { name: '200', value: '200' },
      { name: '204', value: '204' },
      { name: '400', value: '400' },
      { name: '401', value: '401' },
      { name: '403', value: '403' },
      { name: '404', value: '404' },
      { name: '405', value: '405' },
      { name: '406', value: '406' },
      { name: '409', value: '409' },
      { name: '415', value: '415' },
      { name: '424', value: '424' },
      { name: '500', value: '500' },
      { name: '502', value: '502' },
      { name: '503', value: '503' },
      { name: '504', value: '504' },
      { name: '-100', value: '-100' },
      { name: '-200', value: '-200' },
    ],
    responseCode: '',
    direction_list: [
      { name: 'All', value: ' ' },
      { name: 'outbound', value: '11' },
      { name: 'inbound', value: '10' },
    ],
    direction: '',
    protocol_list: [
      { name: 'All', value: ' ' },
      { name: 'http', value: '10' },
      { name: 'sqs', value: '11' },
    ],
    protocol: '',
    currentCleanUpTime: 0,
    setCleanUpTime: 3,
    showCleanUpDialog: false,
    showErrorMessage: false,
    showRequestBody: true,
    showResponseBody: false,
    errorMessage: '',
    requestBody: '',
    responseBody: '',
    isRequestBodyJson: false,
    isResponseBodyJson: false,
    isStateReport: false,
    isPreferredUsernameValue: false,
    preferredUsernameValue: '',
    decodeStateReport: {},
    total: 0,
    totalCount: 0,
    pageCount: 0,
    vin: '',
    traceId: '',
    requestUri: '',
    keyword: '',
    urlKeyword: '',
    payloadKeyword: '',
    searchLoading: false,
    reloadLoading: false,
    downloadLoading: false,
    visible: false,
    valid: false,
    startDate: '',
    endDate: '',
    logItemExpanded: [],
    options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [false], groupBy: [], groupDesc: [], mustSort: false, multiSort: false },
    oldOptions: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [false], groupBy: [], groupDesc: [], mustSort: false, multiSort: false },
    inputPageValue: '',
    inputPageRules: [
      (v) => /^\d*$/.test(v) || 'input page size',
    ],
    logInformationHeaders: [
      { title: '', key: 'data-table-expand', sortable: false, width: 36 },
      { title: 'Date Time(UTC)', key: 'createdTime', sortable: false, width: 140 },
      { title: 'Vin', key: 'vin', sortable: false, width: 150 },
      { title: 'Trace Id', key: 'traceId', sortable: false, width: 260 },
      { title: 'URI', key: 'requestUri', sortable: false, minWidth: 300 },
      { title: 'Response Code', key: 'responseCode', sortable: false, width: 150 },
      { title: 'Response Time', key: 'responseTime', sortable: false, width: 80 },
      { title: 'Direction', key: 'direction', sortable: false, width: 120 },
      { title: 'Protocol', key: 'protocol', sortable: false, width: 120 },
    ],
    logInformationSet: [],
    footerProps: {
      itemsPerPageOptions: [10, 50, 100],
      prevIcon: null,
      nextIcon: null,
      disablePagination: true,
    },
    vinRules: [
      (v) => ((v.length >= 3 && v.length <= 17) || v.length === 0) || 'VIN min 3 characters max 17 characters',
    ],
    cleanUpSet: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    expandTabs: {},  // Changed to object to store tab state per item id
    expandedData: {},  // Store expanded data per item id
    searchResultCountText: '',
    startMenu: false,
    endMenu: false,
    currentPage: 1,
    itemsPerPageOptions: [10, 50, 100],
  }),

  watch: {
    'options.itemsPerPage': function(newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== undefined) {
        console.log('itemsPerPage changed from', oldVal, 'to', newVal)
        this.options.page = 1
        this.currentPage = 1
        this.inputPageValue = ''
        window.scroll(0, 0)
        this.search()
      }
    }
  },

  created() {
    this.search()
    this.initDateTimePickers()
  },

  methods: {
    async searchData() {
      this.options.page = 1
      this.currentPage = 1
      this.searchLoading = true
      await this.search()
      this.searchLoading = false
    },
    async reload() {
      this.endDate = ''
      this.options.page = 1
      this.currentPage = 1
      this.reloadLoading = true
      await this.search()
      this.reloadLoading = false
    },
    clear() {
      this.startDate = ''
      this.endDate = ''
      this.vin = ''
      this.traceId = ''
      this.requestUri = ''
      this.keyword = ''
      this.responseCode = ''
      this.direction = ''
      this.protocol = ''
      this.urlKeyword = ''
      this.payloadKeyword = ''
      this.visible = false
      this.logInformationSet = []
    },
    clearExpanded() {
      this.logItemExpanded = []
      this.expandTabs = {}
      this.expandedData = {}
      this.requestBody = ''
      this.responseBody = ''
      this.errorMessage = ''
      this.decodeStateReport = {}
    },
    goToPage() {
      if (/^[1-9]\d*$/.test(this.inputPageValue)) {
        const page = parseInt(this.inputPageValue)
        if (page <= this.pageCount) {
          this.currentPage = page
          this.options.page = page
          this.search()
        } else {
          toast.warning('Page number exceeds total pages', 2000)
        }
      } else {
        toast.warning('Invalid page number', 2000)
      }
    },
    async search() {
      this.clearExpanded()
      const requestParameter = {
        startTime: this.startDate ? this.startDate.replace('T', ' ') : '',
        endTime: this.endDate ? this.endDate.replace('T', ' ') : '',
        vin: this.vin,
        traceId: this.traceId,
        requestUri: this.requestUri,
        keyword: this.keyword,
        urlKeyword: this.urlKeyword,
        payloadKeyword: this.payloadKeyword,
        responseCode: this.responseCode,
        direction: this.direction,
        protocol: this.protocol,
        page: this.options.page,
        row: this.options.itemsPerPage,
      }
      console.log('Search with page:', this.options.page, 'row:', this.options.itemsPerPage)
      this.total = 0
      this.searchResultCountText = ', retrieving total count...'
      await businessMonitoringSearch(requestParameter).then((response) => {
        // TOOD unify snackbar
        console.log('Search response:', response)
        // Check if response contains error
        if (response.code && response.code !== 200) {
          toast.error(response.msg || 'Search failed', 5000)
          this.searchResultCountText = ''
          return
        }
        this.logInformationSet = response.logInformationSet || []
        this.total = response.total || 0
        this.pageCount = Math.max(1, Math.ceil(this.total / this.options.itemsPerPage))
        this.currentPage = this.options.page
        this.visible = true
      }).catch((error) => {
        console.error('Search error:', error)
        const errorMsg = error.response?.data?.msg || error.message || 'Search failed'
        toast.error(errorMsg, 5000)
        this.searchResultCountText = ''
      })
      await businessMonitoringCount(requestParameter).then((response) => {
        this.totalCount = response.total
        this.visible = true
        // update displayed search result count
        if (this.totalCount !== null) {
          this.searchResultCountText = ', the total count is ' + this.totalCount
        } else {
          this.searchResultCountText = ''
        }
      }).catch((error) => {
        console.error('Error fetching total count:', error)
        this.searchResultCountText = ''
      })
    },
    reset() {
      this.$refs.form?.reset()
      this.visible = false
    },
    async exportCSV() {
      this.downloadLoading = true
      const requestParameter = {
        startTime: this.startDate ? this.startDate.replace('T', ' ') : '',
        endTime: this.endDate ? this.endDate.replace('T', ' ') : '',
        vin: this.vin,
        traceId: this.traceId,
        requestUri: this.requestUri,
        keyword: this.keyword,
        responseCode: this.responseCode,
        direction: this.direction,
        protocol: this.protocol,
        page: this.options.page,
        row: this.options.itemsPerPage,
      }
      await businessMonitoringExport(requestParameter).then((response) => {
        const blob = new Blob([response], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = url
        const fileName = 'CFESBusinessMonitoringInformation' + new Date().getTime() + '.csv'
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        this.downloadLoading = false
      }).catch(() => {
        this.downloadLoading = false
      })
    },
    async getErrorMessageAndPayload(item) {
      if (!item) return

      const itemId = item.id
      const itemIndex = item.index ?? itemId

      // Initialize tab state for this item
      if (this.expandTabs[itemId] === undefined) {
        this.expandTabs[itemId] = 0
      }

      // Initialize data for this item
      this.expandedData[itemId] = {
        requestBody: '',
        responseBody: '',
        errorMessage: '',
        isRequestBodyJson: false,
        isResponseBodyJson: false,
        isStateReport: false,
        isPreferredUsernameValue: false,
        preferredUsernameValue: '',
        decodeStateReport: {},
        metadata: ''
      }

      await businessMonitoringGetErrorPayload(itemIndex, itemId).then(async (response) => {
        const data = {
          errorMessage: response.errorMessage == null ? 'No Data Available' : response.errorMessage,
          requestBody: response.requestBody == null ? 'No Data Available' : response.requestBody,
          responseBody: response.responseBody == null ? 'No Data Available' : response.responseBody,
          metadata: response.metadata == null ? 'No Data Available' : response.metadata,
          isRequestBodyJson: false,
          isResponseBodyJson: false,
          isStateReport: false,
          isPreferredUsernameValue: response.preferredUsernameValue != null,
          preferredUsernameValue: response.preferredUsernameValue || '',
          decodeStateReport: {}
        }

        try {
          data.requestBody = JSON.parse(data.requestBody)
          data.isRequestBodyJson = true
          if ((item.requestUri === '/statenotification' && data.requestBody?.type === 'reportstate') || item.requestUri === '/ReportState') {
            data.requestBody.type = 'reportstate'
            data.isStateReport = true
            await businessMonitoringDecodeStateReport(data.requestBody).then((resp) => {
              data.decodeStateReport = resp
            })
          }
        } catch (e) {
          data.isRequestBodyJson = false
        }

        try {
          data.responseBody = JSON.parse(data.responseBody)
          data.isResponseBodyJson = true
        } catch (e) {
          data.isResponseBodyJson = false
        }

        try {
          data.metadata = JSON.parse(data.metadata)
          data.isMetadataJson = true
        } catch (e) {
          data.isMetadataJson = false
        }

        this.expandedData[itemId] = data
      })
    },
    onExpandedChange(val) {
      this.logItemExpanded = val
      if (Array.isArray(val) && val.length > 0) {
        const lastId = val[val.length - 1]
        // Vuetify 3 returns id array, need to find the actual item
        const lastItem = typeof lastId === 'object' ? lastId : this.logInformationSet.find((i) => i.id === lastId)
        if (lastItem) {
          // Initialize tab state for this item if not exists
          if (this.expandTabs[lastItem.id] === undefined) {
            this.expandTabs[lastItem.id] = 0
          }
          // Only fetch data if not already loaded
          if (!this.expandedData[lastItem.id]) {
            this.getErrorMessageAndPayload(lastItem)
          }
        }
      }
    },
    goFirstPage() {
      if (this.options.page > 1) {
        this.options.page = 1
        this.inputPageValue = ''
        window.scroll(0, 0)
        this.search()
      }
    },
    goPrevPage() {
      if (this.options.page > 1) {
        this.options.page--
        this.inputPageValue = ''
        window.scroll(0, 0)
        this.search()
      }
    },
    goNextPage() {
      if (this.options.page < this.pageCount) {
        this.options.page++
        this.inputPageValue = ''
        window.scroll(0, 0)
        this.search()
      }
    },
    goLastPage() {
      if (this.options.page < this.pageCount) {
        this.options.page = this.pageCount
        this.inputPageValue = ''
        window.scroll(0, 0)
        this.search()
      }
    },
    onItemsPerPageChange(val) {
      console.log('onItemsPerPageChange called with val:', val)
      if (!val) return
      this.options.itemsPerPage = val
      this.options.page = 1
      this.currentPage = 1
      this.inputPageValue = ''
      window.scroll(0, 0)
      this.search()
    },

    cancelCleanUpDialog() {
      this.showCleanUpDialog = false
    },
    async saveCleanUpDialog() {
      await businessMonitoringCleanUp(this.setCleanUpTime).then(() => {
        toast.success('Success', 2000)
        this.showCleanUpDialog = false
      })
    },
    getCleanUpTime() {
      businessMonitoringGetCleanUp().then((response) => {
        this.currentCleanUpTime = response.cleanUp
      })
    },
    formatDate(value) {
      return moment.utc(value).format('YYYY-MM-DD HH:mm:ss')
    },
    pasteVinFilter(event) {
      event.preventDefault()
      const clipped = event.clipboardData.getData('text')
      this.vin = clipped.trim()
    },
    applyStart() {
      this.startMenu = false
    },
    applyEnd() {
      this.endMenu = false
    },
    clearStart() {
      this.startDate = ''
    },
    clearEnd() {
      this.endDate = ''
    },
    normalizeDateTime(raw) {
      return raw
    },
    initDateTimePickers() {},
    decodeProofOfPurchase(payload, item) {
      item.decodedStr = []
      payload.licenseAssignmentRequest.licenceAssignment.forEach((licenceAssignment) => {
        item.decodedStr.push(JSON.parse(atob(licenceAssignment.proofOfPurchase.split('.')[1])))
      })
    },
    decodeProofOfRevocation(payload, item) {
      item.decodedStr = []
      payload.licenseRevocationRequest.licenseRevocation.forEach((licenseRevocation) => {
        item.decodedStr.push(JSON.parse(atob(licenseRevocation.proofOfRevocation.split('.')[1])))
      })
    },
    // Termination requests can carry BOTH proofOfPurchase and proofOfRevocation
    // in the payload (the exact wrapper key varies between clients, e.g.
    // `licenseTerminationRequest.licenseTermination[]` or plain `terminations[]`),
    // so we walk the payload recursively and decode every JWT-shaped
    // `proofOfPurchase` / `proofOfRevocation` string we find, preserving the
    // order they appear in the payload.
    decodeTermination(payload, item) {
      item.decodedStr = []
      if (!payload || typeof payload !== 'object') return

      const decodeJwtPayload = (jwt) => {
        if (typeof jwt !== 'string' || !jwt.includes('.')) return null
        try {
          // base64url -> base64
          const b64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
          return JSON.parse(decodeURIComponent(escape(atob(b64))))
        } catch (_) {
          try {
            return JSON.parse(atob(jwt.split('.')[1]))
          } catch (_e) {
            return { error: 'Failed to decode JWT', raw: jwt }
          }
        }
      }

      const walk = (node) => {
        if (!node) return
        if (Array.isArray(node)) {
          node.forEach(walk)
          return
        }
        if (typeof node !== 'object') return

        if (node.proofOfPurchase) {
          item.decodedStr.push({ type: 'proofOfPurchase', decoded: decodeJwtPayload(node.proofOfPurchase) })
        }
        if (node.proofOfRevocation) {
          item.decodedStr.push({ type: 'proofOfRevocation', decoded: decodeJwtPayload(node.proofOfRevocation) })
        }
        Object.values(node).forEach((v) => {
          if (v && typeof v === 'object') walk(v)
        })
      }

      walk(payload)
    },
    copyToClipboard,
  },
}
</script>

<style scoped>
.page-shell {
  padding: 8px 0 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 0;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.filter-title {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 4px 0;
}

.filter-form :deep(.v-field) {
  font-size: 0.8rem;
}

.filter-form :deep(.v-field__input) {
  font-size: 0.8rem;
  min-height: 32px;
  padding: 4px 8px;
}

.filter-form :deep(.v-label) {
  font-size: 0.75rem;
}

.filter-form :deep(.v-select__selection-text) {
  font-size: 0.8rem;
}

.filter-input :deep(.v-field) {
  --v-field-padding-start: 8px;
  --v-field-padding-end: 8px;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(table) {
  width: 100%;
  table-layout: auto;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.word-break {
  word-break: break-all;
  max-width: 100%;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.info-title {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  height: 54px;
  padding: 0 6px;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > th {
  padding: 0 6px;
}

::v-deep .v-label {
  font-size: 0.75rem;
}

.uri-cell {
  word-break: break-all;
  min-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}

::v-deep .v-select__selection {
  font-size: 0.75rem;
}

.header-select {
  min-width: 100px;
  font-size: 0.75rem;
}

.header-select :deep(.v-field__input) {
  padding: 4px 8px;
  min-height: 32px;
}

.header-select :deep(.v-select__selection-text) {
  font-size: 0.75rem;
}

.header-filter-select {
  min-width: 120px;
  max-width: 150px;
}

.header-filter-select :deep(.v-field) {
  font-size: 0.7rem;
}

.header-filter-select :deep(.v-field__input) {
  font-size: 0.7rem;
  padding: 4px 8px;
  min-height: 28px;
}

.header-filter-select :deep(.v-select__selection-text) {
  font-size: 0.7rem;
}

.header-filter-select :deep(.v-field__append-inner) {
  padding-top: 4px;
}

.header-filter-select :deep(.v-label) {
  font-size: 0.7rem;
  font-weight: 600;
}
</style>
<style scoped>
:deep(.v-data-table-header th) {
  background-color: #f1f5f9;
  color: #111827;
  font-weight: 600;
  font-size: 0.7rem;
}

:deep(.v-data-table thead th) {
  font-size: 0.7rem;
}
</style>
